import React from 'react';
import SaveButton, { SaveButtonProps } from '../UI/SaveButton';
import { SaveStatus } from '../../hooks/useBCPSavingStrategy';

export interface BCPSaveButtonProps extends Omit<SaveButtonProps, 'onSave'> {
  section: string;
  onSave: () => Promise<void>;
  savingStatus: SaveStatus;
  disabled?: boolean;
}

const BCPSaveButton: React.FC<BCPSaveButtonProps> = ({
  section,
  onSave,
  savingStatus,
  disabled = false,
  ...saveButtonProps
}) => {
  const isSaving = savingStatus.isManualSaving[section];
  const hasError = savingStatus.errors[section];
  const lastSaved = savingStatus.lastSaved;

  // Determine the current state based on save status
  const getCurrentState = (): 'idle' | 'saving' | 'success' | 'error' => {
    if (isSaving) return 'saving';
    if (hasError) return 'error';
    if (lastSaved) return 'success';
    return 'idle';
  };

  // Get success message with timestamp
  const getSuccessMessage = () => {
    if (lastSaved) {
      return `Saved at ${lastSaved.toLocaleTimeString()}`;
    }
    return 'Saved successfully!';
  };

  // Get error message
  const getErrorMessage = () => {
    if (hasError) {
      return typeof hasError === 'string' ? hasError : 'Save failed. Please try again.';
    }
    return 'Save failed. Please try again.';
  };

  return (
    <div className="flex flex-col items-end space-y-2">
      <SaveButton
        onSave={onSave}
        disabled={disabled || isSaving}
        successMessage={getSuccessMessage()}
        errorMessage={getErrorMessage()}
        successDuration={3000} // Show success for 3 seconds
        showSuccessFeedback={true}
        showErrorFeedback={true}
        size="md"
        variant="primary"
        {...saveButtonProps}
      />

      {/* Additional status indicator for more detailed feedback */}
      {isSaving && (
        <div className="flex items-center text-blue-600 text-xs">
          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-1"></div>
          Saving to server...
        </div>
      )}

      {hasError && (
        <div className="flex items-center text-red-600 text-xs">
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {getErrorMessage()}
        </div>
      )}
    </div>
  );
};

export default BCPSaveButton;
